import React from 'react';

import { useForm } from 'react-hook-form';
import { Button, InputView, LbSelect } from '@infoods/lifebase-ui';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import classes from './IngredientDetailsModal.module.scss';
import { LbNumericFormatInput } from '../../../../../shared/components/form-components';
import { LbBigModal } from '../../../../../shared/components/modals';
import { Nutrient } from '../../../../../shared/components/ui-components';
import { openConfirmationModal } from '../../../../../shared/features/confirmation';
import { getRoundedNumber } from '../../../../../shared/utils/mathUtils';
import { getMeasurementOptions } from '../../../../utils';
import {
  INGREDIENT_ROUNDING_PRECISION,
  RECIPE_MACROS,
} from '../../constants/recipeMacros';
import { IngredientDetailsFormValues } from '../../interfaces/IngredientDetailsFormValues';
import { RecipeIngredientFormValue } from '../../interfaces/RecipeFormValues';
import { getIngredientTotals } from '../../utils/getIngredientTotals/getIngredientTotals';
import { ingredientFromModal } from '../../utils/ingredientFromModal/ingredientFromModal';
import { VegetableTabs } from './VegetableTabs/VegetableTabs';

type IngredientDetailsModalProps = {
  /** the form value - not `fields[i]`, whose field-array key `update` would store */
  ingredient: RecipeIngredientFormValue;
  onSave: (ingredient: RecipeIngredientFormValue) => void;
  onClose: () => void;
} & ({ mode: 'add'; onDelete?: never } | { mode: 'edit'; onDelete: () => void });

const modeTexts = {
  add: { title: 'Add Ingredient', yesText: 'Add' },
  edit: { title: 'Edit Ingredient', yesText: 'Save' },
} as const;

const MIN_AMOUNT = 0.01;
const MAX_AMOUNT = 9999;
const AMOUNT_DECIMAL_SCALE = 2;
const DISPLAY_ROUNDING_PRECISION = 1;

const amountRangeMessage = `Value must be between ${MIN_AMOUNT} and ${MAX_AMOUNT}`;

/** Mount it only while open - the form reads `ingredient` once, on mount */
const IngredientDetailsModal = ({
  mode,
  ingredient,
  onSave,
  onDelete,
  onClose,
}: IngredientDetailsModalProps) => {
  const { control, handleSubmit, watch } = useForm<IngredientDetailsFormValues>({
    defaultValues: {
      amount: ingredient.amount,
      amountUnit: ingredient.amountUnit,
      ...(mode === 'add' && {
        isVegetable: ingredient.perUnit.veggies > 0,
        veggiesCups: getRoundedNumber(
          ingredient.perUnit.veggies * ingredient.amount,
          INGREDIENT_ROUNDING_PRECISION,
        ),
      }),
    },
  });

  const { amount, amountUnit, isVegetable, veggiesCups } = watch();

  const next = ingredientFromModal(
    ingredient,
    { amount, amountUnit, isVegetable, veggiesCups },
    mode,
  );
  const previewTotals = getIngredientTotals(next.perUnit, next.amount);

  const generalInputProps = {
    control,
    size: 'medium',
    variant: 'standard',
  } as const;

  const handleSave = handleSubmit((formValues) => {
    onSave(ingredientFromModal(ingredient, formValues, mode));
    onClose();
  });

  const handleDeleteClick = (handleDelete: () => void) =>
    openConfirmationModal({
      title: 'Are you sure you want to delete ingredient?',
      confirmBtnConfig: {
        text: 'Delete',
        onClick: () => {
          handleDelete();
          onClose();
        },
      },
    });

  return (
    <LbBigModal
      open
      title={`${modeTexts[mode].title} - "${ingredient.name}"`}
      onClose={onClose}
      buttonOptions={{
        noHandler: onClose,
        noText: 'Cancel',
        yesHandler: handleSave,
        yesText: modeTexts[mode].yesText,
      }}
      extraButton={
        onDelete && (
          <Button
            variant="outlined"
            size="medium"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => handleDeleteClick(onDelete)}
            data-test-id="delete-ingredient-btn"
          >
            Delete
          </Button>
        )
      }
    >
      {mode === 'add' && <VegetableTabs control={control} />}
      <div className={classes.formFieldsContainer}>
        {ingredient.isNetwork ? (
          <LbSelect
            {...generalInputProps}
            name="amountUnit"
            label="Measurement"
            options={getMeasurementOptions(
              ingredient.measurements.map(({ unit }) => unit),
              ingredient.amountUnit,
            )}
            rules={{ required: 'Measurement is required' }}
          />
        ) : (
          <InputView label="Measurement" size="medium" value={ingredient.amountUnit} />
        )}
        <LbNumericFormatInput
          {...generalInputProps}
          name="amount"
          label="Choose amount"
          rules={{
            required: 'Amount is required',
            min: { value: MIN_AMOUNT, message: amountRangeMessage },
            max: { value: MAX_AMOUNT, message: amountRangeMessage },
          }}
          allowNegative={false}
          decimalScale={AMOUNT_DECIMAL_SCALE}
          selectValueOnFocus
        />
        {mode === 'add' && isVegetable && (
          <LbNumericFormatInput
            {...generalInputProps}
            name="veggiesCups"
            label="Veggies amount (cups)"
            rules={{
              required: 'Veggies amount is required',
              validate: (value) => Number(value) > 0 || 'Value must be greater than 0',
            }}
            allowNegative={false}
            decimalScale={INGREDIENT_ROUNDING_PRECISION}
            selectValueOnFocus
          />
        )}
      </div>
      <div className={classes.preview} data-test-id="ingredient-preview">
        {RECIPE_MACROS.map(({ key, label, nutrient }) => (
          <Nutrient key={key} nutrient={nutrient}>
            <span className={classes.previewNutrient}>
              <span className={classes.previewLabel}>{label}</span>
              <span className={classes.previewValue}>
                {getRoundedNumber(previewTotals[key], DISPLAY_ROUNDING_PRECISION)}
              </span>
            </span>
          </Nutrient>
        ))}
      </div>
    </LbBigModal>
  );
};

export { IngredientDetailsModal };
